// YARN
import { Button, ThemeProvider, Box, ClickAwayListener, Tooltip, Badge } from '@mui/material';
import { GridToolbarContainer } from '@mui/x-data-grid-pro';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import ZoomInMapIcon from '@mui/icons-material/ZoomInMap';

// SERVICES
import brushLogo from 'assets/images/brush.svg';

// THEMES
import { customToolbarButtonTheme } from 'assets/styles/themes/generic_button_mui';

type CustomToolbarEffectsParams = {
  callAPI?: ((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void) | undefined;
  allowSubmitEffect?: boolean;
  allowSubmitEffectBrush?: boolean;
  handleBrushEffect?:
    | ((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void)
    | undefined;
  fullScreen: boolean;
  handleFullScreen?: React.MouseEventHandler<SVGSVGElement> | undefined;
  handleResetGroundTypeEffects?: (() => void) | undefined;
  fromGroundTypeEffects?: boolean;
  from?: string;
};

export function CustomToolbarEffects({
  callAPI,
  allowSubmitEffect,
  allowSubmitEffectBrush,
  handleBrushEffect,
  fullScreen,
  handleFullScreen,
  handleResetGroundTypeEffects,
  fromGroundTypeEffects,
  from,
}: CustomToolbarEffectsParams) {
  return (
    <>
      {!fullScreen && (
        <>
          <GridToolbarContainer>
            <ThemeProvider theme={customToolbarButtonTheme}>
              <div>
                <Button disabled={!allowSubmitEffect} onClick={callAPI}>
                  Enregistrer
                </Button>
              </div>
              <div>
                <Button
                  disabled={!allowSubmitEffectBrush}
                  onClick={handleBrushEffect}
                  sx={{ ml: 2 }}
                >
                  Terminer
                  <span>
                    <img src={brushLogo} alt="Logo pinceau" className="brush_logo" />
                  </span>
                </Button>
              </div>
              {fromGroundTypeEffects && from === 'exploitations' && (
                <div>
                  <Button onClick={handleResetGroundTypeEffects}>
                    Remettre les valeurs par défaut
                  </Button>
                </div>
              )}
              <div className="contain_switch_effect">
                {fullScreen ? (
                  <ZoomInMapIcon onClick={handleFullScreen} />
                ) : (
                  <ZoomOutMapIcon onClick={handleFullScreen} />
                )}
              </div>
            </ThemeProvider>
          </GridToolbarContainer>
        </>
      )}
    </>
  );
}

type CustomTooltipEffectsParams = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleTooltipToggle: () => void;
  brushValueList: Array<{ number: number; color: string }>;
  selectedBrushValue: string | null;
  handleClickValue: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  modeBrush: boolean;
  setSelectedBrushValue: React.Dispatch<React.SetStateAction<string | null>>;
};

type HandleClickBrush = (event: React.MouseEvent<HTMLImageElement, MouseEvent>) => void;

export function CustomTooltipEffects({
  open,
  setOpen,
  handleTooltipToggle,
  brushValueList,
  selectedBrushValue,
  handleClickValue,
  modeBrush,
  setSelectedBrushValue,
}: CustomTooltipEffectsParams) {
  const foundBrushValue = brushValueList?.find(
    (brushValue) => brushValue?.number?.toString() === selectedBrushValue
  );

  const selectedIndex = foundBrushValue
    ? brushValueList?.findIndex((brushValue) => brushValue?.number === foundBrushValue?.number)
    : -1;

  const handleClickBrush: HandleClickBrush = (event) => {
    if (!selectedBrushValue) {
      const firstBrushValue = brushValueList[0]?.number?.toString();

      handleClickValue(event);
      setSelectedBrushValue(firstBrushValue);
    } else {
      handleClickValue(event);
    }
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box className="tooltip-wrapper-brush-effect">
        <Tooltip
          id="custom-tooltip"
          slotProps={{
            tooltip: {
              sx: {
                minWidth: 485,
              },
            },
          }}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          open={open}
          onClose={() => setOpen(false)}
          title={
            <Box className="custom-tooltip-brush__list">
              {brushValueList &&
                brushValueList.map((brushValue, index) => (
                  <Badge
                    key={brushValue.number}
                    badgeContent={'✓'}
                    color="primary"
                    invisible={selectedBrushValue !== brushValue?.number?.toString()}
                  >
                    <span
                      className={`custom-tooltip-brush__list--item ${
                        selectedBrushValue === (brushValue.number as number).toString()
                          ? 'custom-tooltip-brush__list-item--selected'
                          : ''
                      }`}
                      onClick={handleClickValue}
                      style={{
                        backgroundColor: `color-mix(in srgb, ${brushValue.color} 100%, white)`,
                        color: index >= 3 && index < 6 ? 'black' : 'white',
                        cursor: modeBrush ? '' : 'pointer',
                      }}
                    >
                      {brushValue.number}
                    </span>
                  </Badge>
                ))}
            </Box>
          }
          placement="bottom-start"
        >
          <Box className="tooltip-wrapper-brush-effect__selection" onClick={handleTooltipToggle}>
            {!selectedBrushValue ? (
              <Box className="tooltip-wrapper-brush-effect__selection--box">
                <img src={brushLogo} alt="Logo pinceau" onClick={(e) => handleClickBrush(e)} />
                <Badge
                  key={foundBrushValue?.number}
                  badgeContent={'✓'}
                  color="primary"
                  invisible={selectedBrushValue !== foundBrushValue?.number.toString()}
                >
                  <span
                    className="tooltip-wrapper-brush-effect__selection--item"
                    style={{
                      backgroundColor: `color-mix(in srgb, ${
                        brushValueList[0]?.color?.toString() ?? 'defaultColor'
                      } 100%, white)`,
                      color: selectedIndex >= 3 && selectedIndex < 6 ? 'black' : 'white',
                      padding: '5px',
                      cursor: 'pointer',
                    }}
                  >
                    {brushValueList[0]?.number?.toString()}
                  </span>
                </Badge>
              </Box>
            ) : (
              <Box className="tooltip-wrapper-brush-effect__selection--box">
                <img src={brushLogo} alt="Logo pinceau" />
                {brushValueList && foundBrushValue && (
                  <Badge
                    key={foundBrushValue?.number}
                    badgeContent={'✓'}
                    color="primary"
                    invisible={selectedBrushValue !== foundBrushValue?.number.toString()}
                  >
                    <span
                      className="tooltip-wrapper-brush-effect__selection--item"
                      style={{
                        backgroundColor: `color-mix(in srgb, ${
                          foundBrushValue?.color ?? 'defaultColor'
                        } 100%, white)`,
                        color: selectedIndex >= 3 && selectedIndex < 6 ? 'black' : 'white',
                        padding: '5px',
                        cursor: 'pointer',
                      }}
                    >
                      {foundBrushValue?.number}
                    </span>
                  </Badge>
                )}
              </Box>
            )}
          </Box>
        </Tooltip>
      </Box>
    </ClickAwayListener>
  );
}
